import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { db, collection, getDocs, query, where, handleFirestoreError, OperationType, doc, getDoc } from '@/firebase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, User, ArrowLeft, Share2, Facebook, Twitter, MessageCircle, ArrowRight, Clock, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import ArticleContentRenderer from '@/components/ArticleContentRenderer';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { formatDateSafe, toIsoStringSafe } from '@/utils/formatters';
import AdUnit from '@/components/AdUnit';

interface Article {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  imageUrl: string;
  category: string;
  authorName: string;
  createdAt: any;
  status?: string;
  relatedProductId?: string;
}

// Helper to strip markdown and extract a clean plain-text description for meta tags
function getCleanExcerpt(content?: string, maxLength = 160): string {
  if (!content) return '';
  return content
    .replace(/!\[.*?\]\(.*?\)/g, '') // remove images
    .replace(/\[(.*?)\]\(.*?\)/g, '$1') // unwrap links
    .replace(/[#*`_~>\-]/g, ' ') // remove markdown syntax characters
    .replace(/\s+/g, ' ') // collapse whitespaces
    .trim()
    .slice(0, maxLength);
}

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  desc: string;
  image: string;
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { isAdmin } = useAuth();
  const [article, setArticle] = React.useState<Article | null>(null);
  const [relatedProduct, setRelatedProduct] = React.useState<Product | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchArticle = async () => {
      try {
        // If admin, we can query without status filter to preview drafts
        // If not admin, MUST filter by 'published' to satisfy security rules
        let q;
        if (isAdmin) {
          q = query(collection(db, 'articles'), where('slug', '==', slug));
        } else {
          q = query(
            collection(db, 'articles'), 
            where('slug', '==', slug),
            where('status', '==', 'published')
          );
        }
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const docSnap = querySnapshot.docs[0];
          const articleData = { id: docSnap.id, ...docSnap.data() as any } as Article;
          setArticle(articleData);

          if (articleData.relatedProductId) {
            try {
              const productRef = doc(db, 'products', articleData.relatedProductId);
              const productSnap = await getDoc(productRef);
              if (productSnap.exists()) {
                setRelatedProduct({ id: productSnap.id, ...productSnap.data() as any } as Product);
              }
            } catch (err) {
              console.error("Gagal memuat produk terkait", err);
            }
          }
        }
      } catch (error) {
        handleFirestoreError(error, OperationType.GET, `articles?slug=${slug}`);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article?.title,
        url: window.location.href,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Tautan artikel berhasil disalin!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex items-center justify-center">
        <Helmet>
          <title>Memuat Artikel... | AZMAN204</title>
        </Helmet>
        <div className="animate-pulse text-gold font-bold">Memuat Artikel...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen pt-32 pb-20 flex flex-col items-center justify-center">
        <Helmet>
          <title>Artikel Tidak Ditemukan | AZMAN204</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <h2 className="text-2xl font-bold text-foreground mb-4">Artikel Tidak Ditemukan</h2>
        <Link to="/articles">
          <Button className="bg-gold text-navy font-bold rounded-xl">Kembali ke Artikel</Button>
        </Link>
      </div>
    );
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://azman204.com';
  const canonicalUrl = `${origin}/article/${article.slug}`;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  
  // Ensure image URL is absolute for social media cards (WhatsApp, Facebook, Twitter, Telegram, LinkedIn)
  const metaImageUrl = article.imageUrl
    ? (article.imageUrl.startsWith('http') ? article.imageUrl : `${origin}${article.imageUrl.startsWith('/') ? '' : '/'}${article.imageUrl}`)
    : `${origin}/logo.png`;

  const metaTitle = `${article.title} | AZMAN204`;
  const metaDescription = article.excerpt 
    ? article.excerpt.slice(0, 160) 
    : (getCleanExcerpt(article.content, 155) || `Baca panduan lengkap ${article.title} seputar konstruksi, SMKK, dan tender proyek di AZMAN204.`);

  const isoPublishDate = toIsoStringSafe(article.createdAt);

  const jsonLdData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": metaDescription,
    "image": [metaImageUrl],
    "datePublished": isoPublishDate,
    "dateModified": isoPublishDate,
    "author": {
      "@type": "Person",
      "name": article.authorName || "Tim AZMAN204"
    },
    "publisher": {
      "@type": "Organization",
      "name": "AZMAN204",
      "logo": {
        "@type": "ImageObject",
        "url": `${origin}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "articleSection": article.category || "Konstruksi"
  };

  return (
    <div className="min-h-screen pt-20 pb-20 bg-background transition-colors duration-300">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook / WhatsApp */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="AZMAN204" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImageUrl} />
        <meta property="og:image:alt" content={article.title} />
        <meta property="og:locale" content="id_ID" />
        <meta property="article:published_time" content={isoPublishDate} />
        <meta property="article:author" content={article.authorName || 'AZMAN204'} />
        <meta property="article:section" content={article.category || 'Konstruksi'} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImageUrl} />
        <meta name="twitter:image:alt" content={article.title} />

        {/* Structured Data (JSON-LD) for Search Engines */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLdData)}
        </script>
      </Helmet>

      {/* Back Button - Top Sticky/Floating for Mobile */}
      <div className="max-w-4xl mx-auto px-4 md:px-0 mb-4">
        <Link to="/articles" className="inline-flex items-center text-gold font-bold text-sm hover:gap-2 transition-all group">
          <ArrowLeft className="h-4 w-4 mr-2 group-hover:mr-3 transition-all" /> Kembali ke Daftar Artikel
        </Link>
      </div>

      <article>
        {/* Header Image Section - Now Clean without Title overlay */}
        <div className="max-w-5xl mx-auto md:px-6 mb-8 md:mb-12">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="aspect-[16/9] md:rounded-[3rem] overflow-hidden shadow-2xl shadow-gold/5"
          >
            <img 
              src={article.imageUrl} 
              alt={article.title} 
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
            />
          </motion.div>
        </div>

        {/* Content Section */}
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="bg-gold text-navy font-black uppercase tracking-widest border-none mb-6 px-4 py-1.5 text-[10px]">
              {article.category}
            </Badge>
            
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-foreground mb-8 leading-tight tracking-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs md:text-sm font-bold text-foreground/40 mb-12 pb-8 border-b border-border">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gold" />
                {formatDateSafe(article.createdAt, { day: 'numeric', month: 'long', year: 'numeric' }, 'Terbaru')}
              </span>
              <span className="flex items-center gap-2">
                <User className="h-4 w-4 text-gold" />
                {article.authorName}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gold" />
                {Math.max(1, Math.ceil((article.content || '').split(/\s+/).length / 200))} Menit Baca
              </span>
              <span className="flex items-center gap-2 ml-auto text-gold">
                <BookOpen className="h-4 w-4" />
                Artikel Teknis
              </span>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-12">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <ArticleContentRenderer content={article.content} />
              </motion.div>

              {/* In-Article Google AdSense Unit */}
              <AdUnit className="my-6" />

              {/* Related Product Card */}
              {relatedProduct && (
                <div className="mt-12 md:mt-16 bg-card border border-border p-6 md:p-8 rounded-[2rem] shadow-xl flex flex-col md:flex-row gap-6 md:gap-8 items-center group hover:border-gold/30 transition-colors">
                  <div className="w-full md:w-1/3 aspect-video md:aspect-[4/3] bg-muted rounded-2xl overflow-hidden relative shrink-0">
                    <img src={relatedProduct.image} alt={relatedProduct.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <Badge className="absolute top-3 left-3 bg-card/90 text-foreground backdrop-blur-sm border-none shadow-sm">{relatedProduct.category}</Badge>
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <span className="text-gold font-bold uppercase tracking-widest text-[10px] mb-2 block">Produk Terkait Artikel Ini</span>
                    <h3 className="text-2xl md:text-3xl font-black text-foreground mb-3">{relatedProduct.name}</h3>
                    <p className="text-foreground/60 text-sm mb-6 line-clamp-3">{relatedProduct.desc}</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4">
                      <div className="text-xl md:text-2xl font-black text-primary">
                        Rp {relatedProduct.price?.toLocaleString('id-ID')}
                      </div>
                      <Link to={`/product/${relatedProduct.id}`}>
                        <Button className="bg-gold text-navy hover:bg-gold-light font-bold rounded-xl shadow-lg shadow-gold/20 flex items-center gap-2">
                          Lihat Detail Produk <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Tags & Sharing */}
              <div className="mt-16 pt-12 border-t border-border flex flex-col md:flex-row justify-between items-center gap-8">
              <div className="flex items-center gap-4">
                <span className="text-xs font-black uppercase tracking-widest text-foreground/40">Bagikan:</span>
                <div className="flex gap-2">
                  <button onClick={handleShare} className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground/60 hover:text-gold hover:bg-gold/10 transition-all">
                    <Share2 className="h-4 w-4" />
                  </button>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground/60 hover:text-[#1877F2] hover:bg-[#1877F2]/10 transition-all">
                    <Facebook className="h-4 w-4" />
                  </a>
                  <a href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${article.title}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground/60 hover:text-[#1DA1F2] hover:bg-[#1DA1F2]/10 transition-all">
                    <Twitter className="h-4 w-4" />
                  </a>
                  <a href={`https://wa.me/?text=${encodeURIComponent(article.title + ' ' + window.location.href)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground/60 hover:text-[#25D366] hover:bg-[#25D366]/10 transition-all">
                    <MessageCircle className="h-4 w-4" />
                  </a>
                </div>
              </div>

              <Link to="/articles" className="text-xs font-black uppercase tracking-[0.2em] text-gold hover:text-gold-light flex items-center gap-2">
                Eksplorasi Artikel Lainnya <ArrowLeft className="h-4 w-4 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  </div>
);
}
